import React, { useState, useEffect } from "react";
import {
    Button,
    Box,
    Typography,
    TextField,
    Grid,
    Card,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FaUserFriends, FaHashtag, FaCopy, FaCheckCircle } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from "../../redux/actions/appAction";
import HttpService from '../../services/httpService';
import ResendCustomerLink from './resendCustomerLink';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: "20px",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
    },
    mainCard: {
        maxWidth: "900px",
        margin: "0 auto",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#ffffff",
    },
    header: {
        padding: "24px 30px",
        borderBottom: "1px solid #e0e0e0",
    },
    headerTitle: {
        fontSize: "24px",
        fontWeight: "600",
        color: "#1C375D",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "4px",
    },
    headerSubtitle: {
        fontSize: "14px",
        color: "#666",
    },
    formSection: {
        padding: "30px",
    },
    sectionTitle: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#333",
        marginBottom: "16px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
                borderColor: "#1C375D",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#1C375D",
            },
        },
    },
    submitButton: {
        padding: "10px 32px",
        fontSize: "15px",
        fontWeight: "600",
        textTransform: "none",
        backgroundColor: "#1C375D",
        color: "#ffffff",
        "&:hover": {
            backgroundColor: "#152948",
        },
        "&:disabled": {
            backgroundColor: "#cccccc",
        },
    },
    resetButton: {
        padding: "10px 32px",
        fontSize: "15px",
        fontWeight: "600",
        textTransform: "none",
        border: "1px solid #1C375D",
        color: "#1C375D",
        marginLeft: "8px",
        "&:hover": {
            backgroundColor: "#f5f5f5",
        },
    },
    divider: {
        margin: "24px 0",
    },
    inputIcon: {
        color: "#1C375D",
        fontSize: "18px",
    },
    resultCard: {
        marginTop: "24px",
        padding: "20px",
        backgroundColor: "#f0f9ff",
        borderRadius: "8px",
        border: "1px solid #bae6fd",
    },
    successHeader: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "16px",
    },
    successTitle: {
        fontSize: "18px",
        fontWeight: "600",
        color: "#16a34a",
    },
    linkLabel: {
        fontSize: "14px",
        fontWeight: "500",
        color: "#666",
        marginBottom: "8px",
    },
    linkBox: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 16px",
        backgroundColor: "#ffffff",
        borderRadius: "6px",
        border: "1px solid #e0e0e0",
    },
    linkText: {
        flex: 1,
        fontSize: "13px",
        color: "#333",
        wordBreak: "break-all",
        fontFamily: "monospace",
    },
    copyButton: {
        padding: "8px",
        color: "#1C375D",
        "&:hover": {
            backgroundColor: "#f0f0f0",
        },
    },
    copyButtonSuccess: {
        padding: "8px",
        color: "#16a34a",
    },
    errorBox: {
        marginTop: "16px",
        padding: "12px 16px",
        backgroundColor: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "6px",
    },
    errorText: {
        fontSize: "14px",
        color: "#dc2626",
    },
}));

const ManualReferral = (props) => {
    const classes = useStyles();
    const [formData, setFormData] = useState({
        uniqueleadNumber: "",
        referredByCxName: "",
        referredByCxMobile: "",
        uniqueleadNumberForNewLead: "",
    });
    const [loading, setLoadingState] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);
    const [loadingStep, setLoadingStep] = useState("");
    const [linkType, setLinkType] = useState("referral");
    const [leadNumber, setLeadNumber] = useState("");
    const isReferral = linkType === "referral";
    const isResend = linkType === "resend";

    const switchType = (type) => {
        setLinkType(type);
        setResult(null);
        setError(null);
        setCopied(false);
    };

    const handleGenerateOtherLink = async (e) => {
        e.preventDefault();
        const number = leadNumber.trim();
        if (!leadRegex.test(number)) {
            setError("Please enter a valid Unique Lead Number");
            return;
        }
        setLoadingState(true);
        setError(null);
        setResult(null);
        try {
            const response = await HttpService.manualReferralLeadCreation({ type: linkType, uniqueleadNumber: number });
            if (response?.data?.BankForm) {
                setResult({
                    message: "Bank Form link generated successfully",
                    BankForm: response.data.BankForm,
                });
                props.setSnackInfo("Bank Form Generated Successfully!", "success");
            } else {
                setError("Failed to generate link");
            }
        } catch (err) {
            const errorMessage = err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";
            setError(errorMessage);
            props.setSnackInfo(errorMessage, "error");
        } finally {
            setLoadingState(false);
        }
    };


    const leadRegex = /^[A-Z]{2}\d{7}$/;

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (!leadRegex.test(formData.uniqueleadNumber)) {
                setFormData((prev) => ({
                    ...prev,
                    referredByCxName: "",
                    referredByCxMobile: "",
                }));
                return;
            }

            try {
                const res = await HttpService.getCustomerInfo(
                    formData.uniqueleadNumber
                );

                const data = res.data?.data;

                setFormData((prev) => ({
                    ...prev,
                    referredByCxName: data?.name?.trim() || "",
                    referredByCxMobile: data?.contact_no || "",
                }));

                if (!data?.name || !data?.contact_no) {
                    props.setSnackInfo(
                        "Customer details incomplete. Please enter manually",
                        "warning"
                    );
                }

            } catch (err) {
                setFormData((prev) => ({
                    ...prev,
                    referredByCxName: "",
                    referredByCxMobile: "",
                }));

                props.setSnackInfo(
                    "Customer not found. Please enter manually",
                    "warning"
                );
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [formData.uniqueleadNumber]);

    const handleChange = (e) => {
        let value = e.target.value.trimStart(); // block leading spaces

        // remove extra spaces on blur later
        if (e.target.name === "referredByCxMobile") {
            value = value.replace(/\D/g, ""); // allow only digits
            if (value.length > 10) value = value.slice(0, 10);
        }

        setFormData({
            ...formData,
            [e.target.name]: value,
        });
        setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Final cleanup
        const cleanedData = {
            ...formData,
            uniqueleadNumber: formData.uniqueleadNumber.trim(),
            referredByCxName: formData.referredByCxName.trim(),
            referredByCxMobile: formData.referredByCxMobile.trim(),
            uniqueleadNumberForNewLead: formData.uniqueleadNumberForNewLead.trim(),
        };

        if (cleanedData?.uniqueleadNumber.trim() == cleanedData?.uniqueleadNumberForNewLead.trim()) {
            setError("Referring Lead Number and New Lead Number cannot be the same.");
            props.setSnackInfo("Referring Lead Number and New Lead Number cannot be the same.", "error");
            return;
        }

        // 10 digit validation
        if (!/^\d{10}$/.test(cleanedData.referredByCxMobile)) {
            setError("Please enter a valid 10-digit WhatsApp mobile number");
            props.setSnackInfo("Invalid 10-digit WhatsApp mobile number", "error");
            return;
        }

        setLoadingState(true);
        setError(null);
        setResult(null);

        try {
            setLoadingStep("Updating Lead...");
            const response = await HttpService.manualReferralLeadCreation(cleanedData);
            const data = response?.data;

            if (response.status === 200 && data?.BankForm) {

                // sequential UI messages
                setLoadingStep("Creating referral details...");
                await new Promise((r) => setTimeout(r, 400));

                setLoadingStep("Generating Bank Form link...");
                await new Promise((r) => setTimeout(r, 500));

                setResult(data);
                props.setSnackInfo("Bank Form Generated Successfully!", "success");
                setLoadingStep("");
            } else {
                const errorMessage = data?.message || "Failed to update lead";
                setError(errorMessage);
                props.setSnackInfo(errorMessage, "error");
                setLoadingStep("");
            }
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong. Please try again.";

            setError(errorMessage);
            props.setSnackInfo(errorMessage, "error");
            setLoadingStep("");
        } finally {
            setLoadingState(false);
        }
    };

    const handleReset = () => {
        setFormData({
            uniqueleadNumber: "",
            referredByCxName: "",
            referredByCxMobile: "",
            uniqueleadNumberForNewLead: "",
        });
        setLeadNumber("");
        setResult(null);
        setError(null);
        setCopied(false);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        props.setSnackInfo("Link copied to clipboard!", "success");
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box className={classes.container}>
            <Card className={classes.mainCard}>
                <Box className={classes.header}>
                    <Typography className={classes.headerTitle}>
                        <FaUserFriends />
                        Referral Lead Management
                    </Typography>
                    <Typography className={classes.headerSubtitle}>
                        Update lead information with referral details and generate bank form links.
                    </Typography>
                </Box>

                <Box className={classes.formSection}>
                    <Box mb={3} display="flex" style={{ gap: 8 }}>
                        {[["referral", "Referral"], ["siteVisit", "Site Visit"], ["refund", "Refund"], ["resend", "Resend"]].map(([value, label]) => (
                            <Button
                                key={value}
                                variant={linkType === value ? "contained" : "outlined"}
                                className={linkType === value ? classes.submitButton : classes.resetButton}
                                style={{ marginLeft: 0 }}
                                onClick={() => switchType(value)}
                                disabled={loading}
                            >
                                {label}
                            </Button>
                        ))}
                    </Box>

                        {isResend && (
                            <ResendCustomerLink classes={classes} setSnackInfo={props.setSnackInfo} />
                        )}
                        {!isReferral && !isResend && (
                        <form onSubmit={handleGenerateOtherLink}>
                            <Typography className={classes.sectionTitle}>
                                <FaHashtag className={classes.inputIcon} />
                                {linkType === "siteVisit"
                                    ? "Source lead of the approved Physical Site Visit payment request"
                                    : "Lead for the approved refund"}
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Unique Lead Number"
                                        value={leadNumber}
                                        onChange={(e) => { setLeadNumber(e.target.value.trim()); setError(null); }}
                                        variant="outlined"
                                        required
                                        size="small"
                                        className={classes.textField}
                                    />
                                </Grid>
                            </Grid>
                            {error && (
                                <Box className={classes.errorBox}>
                                    <Typography className={classes.errorText}>{error}</Typography>
                                </Box>
                            )}
                            <Box mt={4} display="flex">
                                <Button type="submit" variant="contained" className={classes.submitButton} disabled={loading}>
                                    {loading ? <CircularProgress size={18} color="inherit" /> : "Generate Bank Form Link"}
                                </Button>
                                <Button variant="outlined" className={classes.resetButton} onClick={handleReset} startIcon={<MdRefresh />}>
                                    Reset
                                </Button>
                            </Box>
                        </form>
                    )}

                    {isReferral && (
                    <form onSubmit={handleSubmit}>
                        {/* Referring Customer Section */}
                        <Typography className={classes.sectionTitle}>
                            <FaUserFriends className={classes.inputIcon} />
                            Referrer Customer Details(Old Customer)
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Referrer  Unique Lead Number"
                                    name="uniqueleadNumber"
                                    value={formData.uniqueleadNumber}
                                    onChange={handleChange}
                                    variant="outlined"
                                    required
                                    size="small"
                                    className={classes.textField}
                                    placeholder="Enter existing customer unique Lead number"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Referrer Customer Name"
                                    name="referredByCxName"
                                    value={formData.referredByCxName}
                                    onChange={handleChange}
                                    variant="outlined"
                                    required
                                    size="small"
                                    className={classes.textField}
                                    placeholder="Enter customer name"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Referrer Customer Mobile"
                                    name="referredByCxMobile"
                                    value={formData.referredByCxMobile}
                                    onChange={handleChange}
                                    variant="outlined"
                                    required
                                    size="small"
                                    className={classes.textField}
                                    placeholder="Enter WhatsApp mobile number"
                                    type="tel"
                                />
                            </Grid>
                        </Grid>

                        <Divider className={classes.divider} />

                        {/* New Lead Section */}
                        <Typography className={classes.sectionTitle}>
                            <FaHashtag className={classes.inputIcon} />
                            New Referred Lead
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="New Unique Lead Number"
                                    name="uniqueleadNumberForNewLead"
                                    value={formData.uniqueleadNumberForNewLead}
                                    onChange={handleChange}
                                    variant="outlined"
                                    required
                                    size="small"
                                    className={classes.textField}
                                    placeholder="Enter new unique Lead number"
                                />
                            </Grid>
                        </Grid>

                        {/* Error Display */}
                        {error && (
                            <Box className={classes.errorBox}>
                                <Typography className={classes.errorText}>
                                    {error}
                                </Typography>
                            </Box>
                        )}

                        {/* Action Buttons */}
                        <Box mt={4} display="flex" gap={2}>
                            <Button
                                type="submit"
                                variant="contained"
                                className={classes.submitButton}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                                        {/* Processing... */}
                                        {loadingStep || "Processing..."}
                                    </>
                                ) : (
                                    "Generate Bank Form Link"
                                )}
                            </Button>
                            <Button
                                variant="outlined"
                                className={classes.resetButton}
                                onClick={handleReset}
                                startIcon={<MdRefresh />}
                            >
                                Reset
                            </Button>
                        </Box>
                    </form>
                    )}

                    {/* Success Result */}
                    {result && (
                        <Box className={classes.resultCard}>
                            <Box className={classes.successHeader}>
                                <FaCheckCircle style={{ color: "#16a34a", fontSize: "22px" }} />
                                <Typography className={classes.successTitle}>
                                    {result.message}
                                </Typography>
                            </Box>
                            <Typography className={classes.linkLabel} style={{ color: "#e41111ff", fontSize: "15px" }}>
                                Copy the link below to share with the referring customer.
                            </Typography>

                            <Box className={classes.linkBox}>
                                <Typography className={classes.linkText}>
                                    {result.BankForm}
                                </Typography>
                                <Tooltip title={copied ? "Copied!" : "Copy link"}>
                                    <IconButton
                                        size="small"
                                        className={copied ? classes.copyButtonSuccess : classes.copyButton}
                                        onClick={() => copyToClipboard(result.BankForm)}
                                    >
                                        {copied ? <FaCheckCircle /> : <FaCopy />}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Card>
        </Box>
    );
};

export default connect(null, { setLoading, setSnackInfo })(ManualReferral);